import baseApi from "./RootBaseApi";
import {
  RevenueReportsApiRaw,
  RevenueReportsNormalized,
  RevenueDataItem,
  DiscountDataItem,
  RevenueFilters,
} from "@/types/revenue-reports.types";

export const RevenueReportsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRevenueReports: builder.query<
      RevenueReportsNormalized,
      RevenueFilters | void
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/revenue",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): RevenueReportsNormalized => {
        const raw = (response as RevenueReportsApiRaw)?.data
          ? (response as RevenueReportsApiRaw).data
          : (response as unknown as RevenueReportsApiRaw["data"]);

        const revenueSrc = (raw as { revenue?: unknown })?.revenue as
          | ({ items?: RevenueDataItem[] } & {
              totalItems?: number;
              currentPage?: number;
              pageLimit?: number;
              totalPages?: number;
            })
          | RevenueDataItem[]
          | undefined;
        const revenueArr: RevenueDataItem[] = Array.isArray(
          (revenueSrc as { items?: RevenueDataItem[] })?.items,
        )
          ? (revenueSrc as { items: RevenueDataItem[] }).items
          : Array.isArray(revenueSrc)
            ? (revenueSrc as RevenueDataItem[])
            : [];
        const revenue = revenueArr.map((r) => ({
          subscription_type: String(r.subscription_type ?? ""),
          period: r.period ?? "",
          period_range: r.period_range ?? undefined,
          net_revenue: Number(r.net_revenue ?? 0),
        }));

        const revenuePagination =
          revenueSrc &&
          typeof revenueSrc === "object" &&
          !Array.isArray(revenueSrc)
            ? (() => {
                const rs = revenueSrc as {
                  totalItems?: number;
                  currentPage?: number;
                  pageLimit?: number;
                  totalPages?: number;
                };
                return {
                  totalItems: Number(rs.totalItems ?? 0),
                  currentPage: Number(rs.currentPage ?? 1),
                  pageLimit: Number(rs.pageLimit ?? (revenueArr.length || 0)),
                  totalPages: Number(rs.totalPages ?? 1),
                };
              })()
            : undefined;

        const discountsSrc = (raw as { discounts?: unknown })?.discounts as
          | { items?: DiscountDataItem[] }
          | DiscountDataItem[]
          | undefined;
        const discountsArr: DiscountDataItem[] = Array.isArray(
          (discountsSrc as { items?: DiscountDataItem[] })?.items,
        )
          ? (discountsSrc as { items: DiscountDataItem[] }).items
          : Array.isArray(discountsSrc)
            ? (discountsSrc as DiscountDataItem[])
            : [];
        const discounts = discountsArr.map((d) => ({
          subscription_type: String(d.subscription_type ?? ""),
          period: d.period ?? "",
          period_range: d.period_range ?? undefined,
          total_discount_amount: Number(d.total_discount_amount ?? 0),
        }));

        return {
          revenue,
          discounts,
          revenuePagination,
        } as RevenueReportsNormalized;
      },
      providesTags: ["RevenueReports"],
      keepUnusedDataFor: 0,
    }),

    getRevenueReportsCsv: builder.query<
      Blob,
      RevenueFilters & { isCsv?: boolean }
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/revenue",
        method: "GET",
        params: { ...(params || {}), isCsv: true },
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useGetRevenueReportsQuery, useLazyGetRevenueReportsCsvQuery } =
  RevenueReportsService;
